import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

// Define Movement History interfaces
export interface MovementHistoryFilters {
  start_date?: string;
  end_date?: string;
  location_id?: string;
  asset_id?: string;
  vehicle_id?: string;
  transfer_id?: string;
  movement_type?: string;
  is_authorized?: boolean;
  page?: number;
  page_size?: number;
}

export interface AssetMovement {
  id: string;
  asset: string;
  asset_detail?: {
    id: string;
    asset_name: string;
    asset_tag: string;
    serial_number?: string;
  };
  reader: string;
  reader_detail?: {
    id: string;
    name: string;
    reader_type: string;
  };
  location: string;
  location_detail?: {
    id: string;
    name: string;
  };
  timestamp: string;
  movement_type: string;
  transfer_request?: string;
  transfer_request_detail?: {
    id: string;
    transfer_number: string;
    status: string;
  };
  is_authorized: boolean;
  details?: Record<string, any>;
}

export interface VehicleMovement {
  id: string;
  vehicle: string;
  vehicle_detail?: {
    id: string;
    name: string;
    registration_number: string;
    epc_id?: string;
  };
  reader: string;
  reader_detail?: {
    id: string;
    name: string;
    reader_type: string;
  };
  location: string;
  location_detail?: {
    id: string;
    name: string;
  };
  timestamp: string;
  movement_type: string;
  transfer_request?: string;
  transfer_request_detail?: {
    id: string;
    transfer_number: string;
    status: string;
  };
  is_authorized: boolean;
  details?: Record<string, any>;
}

export interface MovementHistoryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: (AssetMovement | VehicleMovement)[];
}

// Create API
export const movementHistoryApi = createApi({
  reducerPath: 'movementHistoryApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['MovementHistory'],
  endpoints: (builder) => ({
    getMovementHistory: builder.query<MovementHistoryResponse, MovementHistoryFilters>({
      query: (filters) => {
        // Build query string from filters
        const params = new URLSearchParams();
        
        if (filters.start_date) {
          params.append('start_date', filters.start_date);
        }
        
        if (filters.end_date) {
          params.append('end_date', filters.end_date);
        }
        
        if (filters.location_id) {
          params.append('location_id', filters.location_id);
        }
        
        if (filters.asset_id) {
          params.append('asset_id', filters.asset_id);
        }
        
        if (filters.vehicle_id) {
          params.append('vehicle_id', filters.vehicle_id);
        }
        
        if (filters.transfer_id) {
          params.append('transfer_id', filters.transfer_id);
        }
        
        if (filters.movement_type) {
          params.append('movement_type', filters.movement_type);
        }
        
        if (filters.is_authorized !== undefined) {
          params.append('is_authorized', filters.is_authorized.toString());
        }
        
        if (filters.page) {
          params.append('page', filters.page.toString());
        }
        
        if (filters.page_size) {
          params.append('page_size', filters.page_size.toString());
        }
        
        const queryString = params.toString();
        return `rfid/movements/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['MovementHistory'],
    }),
    
    getAssetMovementHistory: builder.query<MovementHistoryResponse, string>({
      query: (assetId) => `rfid/movements/assets/asset/${assetId}/`,
      providesTags: ['MovementHistory'],
    }),

    getVehicleMovementHistory: builder.query<MovementHistoryResponse, string>({
      query: (vehicleId) => `rfid/movements/vehicles/vehicle/${vehicleId}/`,
      providesTags: ['MovementHistory'],
    }),
    
    getTransferMovementHistory: builder.query<MovementHistoryResponse, string>({
      query: (transferId) => `rfid/movements/transfer/${transferId}/`,
      providesTags: ['MovementHistory'],
    }),
    
    exportMovementHistory: builder.query<Blob, { filters: MovementHistoryFilters; format: 'csv' | 'excel' | 'pdf' }>({
      query: ({ filters, format }) => {
        // Build query string from filters
        const params = new URLSearchParams();
        
        if (filters.start_date) {
          params.append('start_date', filters.start_date);
        }
        
        if (filters.end_date) {
          params.append('end_date', filters.end_date);
        }
        
        if (filters.location_id) {
          params.append('location_id', filters.location_id);
        }
        
        if (filters.asset_id) {
          params.append('asset_id', filters.asset_id);
        }
        
        if (filters.vehicle_id) {
          params.append('vehicle_id', filters.vehicle_id);
        }
        
        if (filters.transfer_id) {
          params.append('transfer_id', filters.transfer_id);
        }
        
        if (filters.movement_type) {
          params.append('movement_type', filters.movement_type);
        }
        
        if (filters.is_authorized !== undefined) {
          params.append('is_authorized', filters.is_authorized.toString());
        }
        
        params.append('format', format);
        
        const queryString = params.toString();
        return {
          url: `rfid/movements/export/${queryString ? `?${queryString}` : ''}`,
          responseHandler: (response:any) => response.blob(),
        };
      },
    }),
  }),
});

// Export hooks
export const {
  useGetMovementHistoryQuery,
  useGetAssetMovementHistoryQuery,
  useGetVehicleMovementHistoryQuery,
  useGetTransferMovementHistoryQuery,
  useLazyExportMovementHistoryQuery,
} = movementHistoryApi;